import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn'
import { RichHtml } from '@/components/ui/RichHtml'
import { wordCount } from '@/lib/richText'

export interface FixedColumn<T> {
  key: string
  header: ReactNode
  /** Largura fixa em px. Sem largura, a coluna fica com o espaço que sobra. */
  width?: number
  align?: 'left' | 'center' | 'right'
  /** Números/valores: alinhados à direita com algarismos tabulares. */
  numeric?: boolean
  render: (row: T) => ReactNode
  className?: string
}

export interface FixedGroup<T> {
  key: string
  /** Conteúdo da linha de grupo (nome, contagem, status, ações do grupo). */
  header?: ReactNode
  rows: T[]
  /** Mostrado quando o grupo não tem linhas. */
  empty?: ReactNode
}

interface Props<T> {
  columns: FixedColumn<T>[]
  /** Linhas soltas — ou use `groups` para separar por categoria/tipo. */
  rows?: T[]
  groups?: FixedGroup<T>[]
  getRowKey: (row: T) => string
  rowClassName?: (row: T) => string | undefined
  loading?: boolean
  emptyMessage?: ReactNode
  /** Largura mínima da tabela; abaixo dela, rola na horizontal. */
  minWidth?: number
  /** Células do rodapé, uma por coluna (ex.: soma dos pesos). */
  footer?: ReactNode[]
  /**
   * Detalhe que abre logo abaixo da linha, na largura toda (descrição longa,
   * cláusula). A página controla o que está aberto: `null` = fechada.
   */
  detail?: (row: T) => ReactNode | null
}

/**
 * Tabela de colunas FIXAS (`table-layout: fixed`). Grupos (categoria, tipo de
 * texto) são linhas dentro da MESMA tabela — as colunas nunca mudam de lugar
 * de um grupo para outro, que era o desalinhamento das tabelas separadas.
 *
 * Regras do sistema: texto nunca é cortado com "…" (quebra linha e a linha
 * cresce); status com texto; números à direita.
 */
export function FixedTable<T>({
  columns, rows, groups, getRowKey, rowClassName, loading, emptyMessage = 'Nenhum registro.', minWidth = 760, footer, detail,
}: Props<T>) {
  const span = columns.length
  const alignOf = (c: FixedColumn<T>) => (c.numeric ? 'right' : c.align ?? 'left')
  const cellAlign = (a: string) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left')

  const renderRows = (list: T[]) => list.map(row => {
    const extra = detail?.(row) ?? null
    return (
      <Fragment key={getRowKey(row)}>
        <tr className={cn(extra != null ? 'bg-brand-50/40' : 'border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60', rowClassName?.(row))}>
          {columns.map(c => (
            <td key={c.key}
              className={cn('h-12 break-words px-4 py-2.5 align-middle text-sm leading-snug text-gray-700',
                cellAlign(alignOf(c)), c.numeric && 'tabular-nums text-gray-900', c.className)}>
              {c.render(row)}
            </td>
          ))}
        </tr>
        {extra != null && (
          <tr className="border-b border-gray-200 bg-brand-50/40">
            <td colSpan={span} className="px-4 pb-5 pt-0">{extra}</td>
          </tr>
        )}
      </Fragment>
    )
  })

  const message = (content: ReactNode) => (
    <tr><td colSpan={span} className="px-4 py-6 text-center text-sm text-gray-500">{content}</td></tr>
  )

  const total = groups ? groups.reduce((n, g) => n + g.rows.length, 0) : rows?.length ?? 0

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse" style={{ minWidth }}>
        <colgroup>
          {columns.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
        </colgroup>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} scope="col" style={c.width ? { width: c.width } : undefined}
                className={cn('h-10 border-b border-gray-200 bg-gray-50 px-4 text-[11px] font-semibold uppercase tracking-wide text-gray-500',
                  cellAlign(alignOf(c)))}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        {loading ? (
          <tbody>{message('Carregando…')}</tbody>
        ) : groups ? (
          total === 0 && groups.every(g => !g.header) ? <tbody>{message(emptyMessage)}</tbody> : groups.map(g => (
            <tbody key={g.key}>
              {g.header && (
                <tr>
                  <td colSpan={span} className="h-[52px] border-y border-gray-200 bg-gray-50/70 px-4 py-2 align-middle">{g.header}</td>
                </tr>
              )}
              {g.rows.length > 0 ? renderRows(g.rows) : g.empty ? (
                <tr><td colSpan={span} className="px-4 py-3 text-sm text-gray-500">{g.empty}</td></tr>
              ) : null}
            </tbody>
          ))
        ) : (
          <tbody>{(rows?.length ?? 0) > 0 ? renderRows(rows!) : message(emptyMessage)}</tbody>
        )}
        {footer && !loading && (
          <tfoot>
            <tr className="border-t border-gray-200 bg-gray-50">
              {footer.map((f, i) => (
                <td key={i} className={cn('h-11 px-4 text-sm font-semibold text-gray-900', columns[i] && cellAlign(alignOf(columns[i])), columns[i]?.numeric && 'tabular-nums')}>{f}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}

/** Botão só de ícone para a coluna de ações (sempre com rótulo para leitor de tela). */
export function RowIconButton({ label, onClick, children, danger, disabled }: {
  label: string
  onClick: () => void
  children: ReactNode
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label}
      className={cn('inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-gray-500 transition disabled:cursor-not-allowed disabled:opacity-40',
        danger ? 'hover:bg-rose-50 hover:text-rose-600' : 'hover:bg-gray-100 hover:text-brand-600')}>
      {children}
    </button>
  )
}

/** Cabeçalho de seção padrão do módulo: título, descrição e ações à direita. */
export function SectionHeader({ title, description, actions, id }: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  id?: string
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 px-6 pb-4 pt-5">
      <div className="min-w-0 space-y-1">
        <h2 id={id} className="text-base font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-[13px] text-gray-600">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/**
 * "Ver texto ›" + tamanho do texto, embaixo do nome na linha. Texto longo não
 * entra na tabela (nem cortado com "…"): abre inteiro com o botão.
 */
export function TextToggle({ open, onToggle, html, kind = 'Texto do PDF', extra, emptyLabel = 'Sem texto para o PDF', labels = ['Ver texto', 'Ocultar texto'] }: {
  open: boolean
  onToggle: () => void
  html: string | null | undefined
  kind?: string
  extra?: ReactNode
  emptyLabel?: string
  labels?: [string, string]
}) {
  const words = wordCount(html)
  if (words === 0) {
    return <p className="text-xs text-amber-700">{emptyLabel}{extra ? <span className="text-gray-500"> · {extra}</span> : null}</p>
  }
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="-ml-2 inline-flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 text-[13px] font-semibold text-brand-600 hover:bg-brand-50">
        <ChevronRight size={14} className={cn('transition-transform', open && 'rotate-90')} />
        {open ? labels[1] : labels[0]}
      </button>
      <span className="text-xs text-gray-500">{kind} · {words} palavra{words === 1 ? '' : 's'}{extra ? <> · {extra}</> : null}</span>
    </div>
  )
}

/** O texto aberto, inteiro e formatado como no PDF, com ações ao lado. */
export function TextPanel({ html, children, aside }: { html?: string | null; children?: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-6 rounded-xl bg-white px-5 py-4 ring-1 ring-brand-100">
      <div className="min-w-0 flex-[1_1_480px] max-w-3xl text-sm leading-relaxed text-gray-800">
        {html != null && <RichHtml html={html} className="[&_p]:mb-2.5 [&_p]:mt-0" />}
        {children}
      </div>
      {aside && <div className="flex flex-[0_0_200px] flex-col gap-2">{aside}</div>}
    </div>
  )
}
